<script setup>
import { onMounted, onUnmounted, ref, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'

const resultsStore = useResultsStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
  resultsStore.fetchResults(route.params.eventAlias)
  resultsStore.fetchEventDetails(route.params.eventAlias)
}, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows
const recentlyChanged = ref({})
let lastResults = []

// Tooltip state
const hoveredCheckpoint = ref(null)
const tooltipPosition = ref({ x: 0, y: 0 })
const isMobile = ref(false)

// Legend popup state
const showLegendPopup = ref(false)

// Sorting state
const sortBy = ref('default') // 'default', 'checkpoints', 'time'
const sortDirection = ref('asc') // 'asc', 'desc'

// Auto-scroll state
const autoScrollEnabled = ref(false)
const autoScrollSpeed = ref(30) // pixels per second
const autoScrollInterval = ref(null)
const tableScrollRef = ref(null)

// Auto-scroll constants
const SCROLL_THRESHOLD = 5 // pixels before bottom to trigger return to top
const PAUSE_AT_TOP_MS = 2000 // milliseconds to pause at top before scrolling again

// Computed property to divide markers into stages
const stages = computed(() => {
  if (!resultsStore.markersMap) return []
  
  const markers = Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
  const stages = []
  let currentStage = []
  
  markers.forEach((marker) => {
    // Skip start markers (we don't display them as checkpoints)
    if (marker.type === 1) return
    
    // If this is a transition zone (type 91), finish current stage
    if (marker.type === 91) {
      if (currentStage.length > 0) {
        stages.push({
          markers: currentStage,
          transitionZone: marker
        })
        currentStage = []
      }
    }
    // If this is a finish marker (type 8 or 93), finish current stage
    else if (marker.type === 8 || marker.type === 93) {
      if (currentStage.length > 0) {
        stages.push({
          markers: currentStage,
          finishMarker: marker
        })
        currentStage = []
      }
    }
    // Regular checkpoint - add to current stage
    else {
      currentStage.push(marker)
    }
  })
  
  // If there are remaining markers and no stages yet, create one stage
  if (currentStage.length > 0) {
    stages.push({
      markers: currentStage,
      finishMarker: null
    })
  }
  
  return stages
})

// Check if this is a single-stage competition
const isSingleStage = computed(() => {
  return stages.value.length === 1
})

// Get total number of checkpoints across all stages
const totalCheckpoints = computed(() => {
  return stages.value.reduce((total, stage) => total + stage.markers.length, 0)
})

// Count completed checkpoints for a runner
function countCompletedCheckpoints(runner) {
  let completed = 0
  stages.value.forEach(stage => {
    stage.markers.forEach(marker => {
      if (isCheckpointCompleted(runner, marker.id)) {
        completed++
      }
    })
  })
  return completed
}

// Count completed stages for a runner (all checkpoints in stage completed)
function countCompletedStages(runner) {
  let completedStages = 0
  stages.value.forEach(stage => {
    const stageCompleted = stage.markers.every(marker => 
      isCheckpointCompleted(runner, marker.id)
    )
    if (stageCompleted) {
      completedStages++
    }
  })
  return completedStages
}

// Get time at the end of the last completed stage
function getTimeAtLastCompletedStage(runner) {
  for (let i = stages.value.length - 1; i >= 0; i--) {
    const stage = stages.value[i]
    const stageCompleted = stage.markers.every(marker => 
      isCheckpointCompleted(runner, marker.id)
    )
    
    if (stageCompleted) {
      // Find the last marker in this stage
      const lastMarker = stage.finishMarker || stage.markers[stage.markers.length - 1]
      if (lastMarker && runner.splits && runner.splits[lastMarker.id]) {
        return new Date(runner.splits[lastMarker.id].time)
      }
    }
  }
  return null // No completed stages
}

// Helper function to handle DNS/DNF sorting
function sortByDNSAndDNF(a, b) {
  if (a.abs) return 1
  if (b.abs) return -1
  if (a.dnf) return 1
  if (b.dnf) return -1
  return 0 // No DNS/DNF, continue with other sorting
}

// Sorted results based on current sorting criteria
const sortedResults = computed(() => {
  const results = [...resultsStore.results]
  
  // For single-stage competitions, determine default sorting
  if (isSingleStage.value && sortBy.value === 'default') {
    // Sort by completed checkpoints (descending), then by time (ascending)
    return results.sort((a, b) => {
      // DNS/DNF always at the bottom
      const dnsDnfSort = sortByDNSAndDNF(a, b)
      if (dnsDnfSort !== 0) return dnsDnfSort
      
      const aCompleted = countCompletedCheckpoints(a)
      const bCompleted = countCompletedCheckpoints(b)
      
      // First, sort by completed checkpoints (descending - more is better)
      if (aCompleted !== bCompleted) {
        return bCompleted - aCompleted
      }
      
      // If equal checkpoints, sort by time (ascending - faster is better)
      const aTime = getTotalTime(a)
      const bTime = getTotalTime(b)
      
      if (aTime && bTime) {
        return aTime.getTime() - bTime.getTime()
      }
      
      // If one has time and other doesn't, prioritize the one with time
      if (aTime) return -1
      if (bTime) return 1
      
      return a.number - b.number
    })
  }
  
  // For multi-stage or manual sorting
  if (sortBy.value === 'checkpoints') {
    return results.sort((a, b) => {
      const dnsDnfSort = sortByDNSAndDNF(a, b)
      if (dnsDnfSort !== 0) return dnsDnfSort
      
      const aCompleted = countCompletedCheckpoints(a)
      const bCompleted = countCompletedCheckpoints(b)
      
      return sortDirection.value === 'desc' 
        ? bCompleted - aCompleted 
        : aCompleted - bCompleted
    })
  }
  
  if (sortBy.value === 'time') {
    return results.sort((a, b) => {
      const dnsDnfSort = sortByDNSAndDNF(a, b)
      if (dnsDnfSort !== 0) return dnsDnfSort
      
      const aTime = getTotalTime(a)
      const bTime = getTotalTime(b)
      
      if (!aTime && !bTime) return a.number - b.number
      if (!aTime) return 1
      if (!bTime) return -1
      
      return sortDirection.value === 'asc'
        ? aTime.getTime() - bTime.getTime()
        : bTime.getTime() - aTime.getTime()
    })
  }
  
  // Default sorting for multi-stage competitions
  if (!isSingleStage.value) {
    return results.sort((a, b) => {
      // DNS/DNF always at the bottom
      const dnsDnfSort = sortByDNSAndDNF(a, b)
      if (dnsDnfSort !== 0) return dnsDnfSort
      
      const aCompletedStages = countCompletedStages(a)
      const bCompletedStages = countCompletedStages(b)
      
      // First, sort by completed stages (descending - more stages completed is better)
      if (aCompletedStages !== bCompletedStages) {
        return bCompletedStages - aCompletedStages
      }
      
      // If equal stages completed, sort by time at last completed stage (ascending - faster is better)
      const aTime = getTimeAtLastCompletedStage(a)
      const bTime = getTimeAtLastCompletedStage(b)
      
      if (aTime && bTime) {
        return aTime.getTime() - bTime.getTime()
      }
      
      // If one has time and other doesn't, prioritize the one with time
      if (aTime) return -1
      if (bTime) return 1
      
      // Finally, sort by start number
      return a.number - b.number
    })
  }
  
  // Fallback: use store's original sorting
  return results
})

// Toggle sorting by column
function toggleSort(column) {
  if (sortBy.value === column) {
    // Toggle direction
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    // Set new column with appropriate default direction
    sortBy.value = column
    sortDirection.value = column === 'checkpoints' ? 'desc' : 'asc'
  }
}

// Computed properties for sort indicators
// Check if we're using default single-stage sorting
const isDefaultSingleStageSorting = computed(() => {
  return sortBy.value === 'default' && isSingleStage.value
})

// For single-stage default sorting, checkpoints are sorted descending with indicator
const showCheckpointsSortIndicator = computed(() => {
  return sortBy.value === 'checkpoints' || isDefaultSingleStageSorting.value
})

const checkpointsSortDirection = computed(() => {
  // Default single-stage sorting uses descending checkpoints
  const isDescending = sortDirection.value === 'desc' || isDefaultSingleStageSorting.value
  return isDescending ? '▼' : '▲'
})

// For single-stage default sorting, time is used as tiebreaker (ascending) with indicator
const showTimeSortIndicator = computed(() => {
  return sortBy.value === 'time' || isDefaultSingleStageSorting.value
})

const timeSortDirection = computed(() => {
  // Default single-stage sorting uses ascending time as tiebreaker
  const isAscending = (sortBy.value === 'time' && sortDirection.value === 'asc') || isDefaultSingleStageSorting.value
  return isAscending ? '▲' : '▼'
})

// Get color for checkpoint based on type
function getCheckpointColor(type) {
  switch (type) {
    case 101: return '#dc3545' // red
    case 102: return '#28a745' // green
    case 103: return '#007bff' // blue
    case 104: return '#6f42c1' // purple
    default: return '#fd7e14' // orange
  }
}

// Check if checkpoint is completed by runner
function isCheckpointCompleted(runner, markerId) {
  return runner.splits && runner.splits[markerId] != null
}

// Get stage time for a runner
function getStageTime(runner, stage, stageIndex) {
  // Determine the end marker for this stage
  let endMarker = stage.transitionZone || stage.finishMarker
  
  if (!endMarker) {
    // No end marker, can't calculate time
    return null
  }
  
  // Get end time - for finish markers, check runner's finish time fields first
  let endTime
  if (stage.finishMarker) {
    // This is the last stage ending at finish
    // Priority: finish_time -> elapsed_finish_time -> finish_time_gps -> splits
    if (runner.finish_time) {
      endTime = new Date(runner.finish_time)
    } else if (runner.elapsed_finish_time) {
      endTime = new Date(runner.elapsed_finish_time)
    } else if (runner.finish_time_gps) {
      endTime = new Date(runner.finish_time_gps)
    } else if (runner.splits && runner.splits[endMarker.id]) {
      endTime = new Date(runner.splits[endMarker.id].time)
    } else {
      // Runner didn't reach finish
      return null
    }
  } else {
    // Transition zone or other stage - use splits
    if (!runner.splits || !runner.splits[endMarker.id]) {
      return null
    }
    endTime = new Date(runner.splits[endMarker.id].time)
  }
  
  // Get start time for this stage
  let startTime
  if (stageIndex === 0) {
    // First stage - use runner start time
    if (!runner.start_time) return null
    startTime = new Date(runner.start_time)
  } else {
    // Not first stage - use previous stage's transition zone time
    const prevStage = stages.value[stageIndex - 1]
    const prevEndMarker = prevStage.transitionZone || prevStage.finishMarker
    if (!prevEndMarker || !runner.splits[prevEndMarker.id]) {
      return null
    }
    startTime = new Date(runner.splits[prevEndMarker.id].time)
  }
  
  const elapsed = endTime - startTime
  return new Date(elapsed)
}

// Get total time for a runner
function getTotalTime(runner) {
  if (runner.finish_time) {
    const finishTime = new Date(runner.finish_time)
    const startTime = new Date(runner.start_time)
    return new Date(finishTime - startTime)
  }
  
  if (runner.finish_time_gps) {
    const finishTime = new Date(runner.finish_time_gps)
    const startTime = new Date(runner.start_time)
    return new Date(finishTime - startTime)
  }
  
  if (runner.elapsed_finish_time) {
    return new Date(runner.elapsed_finish_time)
  }
  
  return null
}

// Check if a runner has finished the race
function hasFinished(runner) {
  return getTotalTime(runner) !== null
}

// Get finished runners from sorted results
const finishedRunners = computed(() => {
  return sortedResults.value.filter(runner => hasFinished(runner))
})

// Get top 3 finishers (runners who have finished the race)
const top3Finishers = computed(() => {
  if (!autoScrollEnabled.value) {
    return []
  }
  
  return finishedRunners.value.slice(0, 3)
})

// Get remaining results (excluding locked top 3)
const scrollableResults = computed(() => {
  if (!autoScrollEnabled.value) {
    return sortedResults.value
  }
  
  // If there are no finished runners, all results are scrollable
  if (finishedRunners.value.length === 0) {
    return sortedResults.value
  }
  
  // Skip the first 3 finished runners
  const top3Ids = new Set(finishedRunners.value.slice(0, 3).map(r => r.id))
  return sortedResults.value.filter(runner => !top3Ids.has(runner.id))
})

// Create a mapping of result IDs to their positions for efficient lookup
const resultPositions = computed(() => {
  const positions = new Map()
  sortedResults.value.forEach((result, index) => {
    positions.set(result.id, index + 1)
  })
  return positions
})

// Format time as HH:MM:SS
function getHHMMSSString(date) {
  if (!date) return '--:--:--'
  const timestamp = date instanceof Date ? date.getTime() : date
  // Handle negative or zero elapsed time - don't display
  if (timestamp <= 0) {
    return ''
  }
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

// Get checkpoint time
function getCheckpointTime(runner, markerId) {
  if (!runner.splits || !runner.splits[markerId]) return null
  return new Date(runner.splits[markerId].time)
}

// Handle checkpoint hover
function handleCheckpointHover(event, runner, marker) {
  if (isMobile.value) return // Don't show tooltip on mobile via hover
  
  const time = getCheckpointTime(runner, marker.id)
  if (!time) return
  
  hoveredCheckpoint.value = {
    markerId: marker.id,
    runnerId: runner.id,
    markerText: marker.text,
    time: time.toLocaleTimeString()
  }
  
  tooltipPosition.value = {
    x: event.clientX,
    y: event.clientY
  }
}

function handleCheckpointLeave() {
  if (!isMobile.value) {
    hoveredCheckpoint.value = null
  }
}

// Handle checkpoint click for mobile
function handleCheckpointClick(runner, marker) {
  if (!isMobile.value) return
  
  const time = getCheckpointTime(runner, marker.id)
  if (!time) return
  
  hoveredCheckpoint.value = {
    markerId: marker.id,
    runnerId: runner.id,
    markerText: marker.text,
    time: time.toLocaleTimeString()
  }
}

function closeTooltip() {
  hoveredCheckpoint.value = null
}

// Watch for result changes
import { watch } from 'vue'
watch(
  () => resultsStore.results,
  (newResults) => {
    const nowTime = Date.now()
    if (lastResults.length === 0) {
      lastResults = newResults.map(r => ({ 
        id: r.id, 
        score: r.score, 
        finish_time: r.finish_time, 
        elapsed_finish_time: r.elapsed_finish_time, 
        finish_time_gps: r.finish_time_gps, 
        dnf: r.dnf, 
        abs: r.abs 
      }))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (!prev || 
          prev.score !== r.score ||
          prev.finish_time !== r.finish_time ||
          prev.elapsed_finish_time !== r.elapsed_finish_time ||
          prev.finish_time_gps !== r.finish_time_gps ||
          prev.dnf !== r.dnf ||
          prev.abs !== r.abs) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = newResults.map(r => ({ 
      id: r.id, 
      score: r.score, 
      finish_time: r.finish_time, 
      elapsed_finish_time: r.elapsed_finish_time, 
      finish_time_gps: r.finish_time_gps, 
      dnf: r.dnf, 
      abs: r.abs 
    }))
  },
  { immediate: true }
)

// Search functionality
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }
  
  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return nameMatch || numberMatch
  }).slice(0, 10)
})

watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false
  
  recentlyChanged.value[result.id] = Date.now()
  
  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  })
}

function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
  if (isMobile.value && !event.target.closest('.checkpoint') && !event.target.closest('.tooltip-modal')) {
    hoveredCheckpoint.value = null
  }
  if (!event.target.closest('.legend-button') && !event.target.closest('.legend-popup') && !event.target.classList.contains('legend-backdrop')) {
    showLegendPopup.value = false
  }
}

function toggleLegendPopup() {
  showLegendPopup.value = !showLegendPopup.value
}

function closeLegendPopup() {
  showLegendPopup.value = false
}

// Auto-scroll functions
function toggleAutoScroll() {
  autoScrollEnabled.value = !autoScrollEnabled.value
  
  if (autoScrollEnabled.value) {
    startAutoScroll()
  } else {
    stopAutoScroll()
  }
}

function startAutoScroll() {
  if (autoScrollInterval.value) return
  
  if (!tableScrollRef.value) return
  
  autoScrollInterval.value = setInterval(() => {
    if (!autoScrollEnabled.value) {
      stopAutoScroll()
      return
    }
    
    const currentScroll = tableScrollRef.value.scrollTop
    const maxScroll = tableScrollRef.value.scrollHeight - tableScrollRef.value.clientHeight
    
    if (currentScroll >= maxScroll - SCROLL_THRESHOLD) {
      // Reached bottom, return to top
      tableScrollRef.value.scrollTo({ top: 0, behavior: 'smooth' })
      // Wait a bit before starting to scroll down again
      setTimeout(() => {
        // Continue scrolling after pause
      }, PAUSE_AT_TOP_MS)
    } else {
      tableScrollRef.value.scrollTop = currentScroll + 1
    }
  }, 1000 / autoScrollSpeed.value)
}

function stopAutoScroll() {
  if (autoScrollInterval.value) {
    clearInterval(autoScrollInterval.value)
    autoScrollInterval.value = null
  }
}

// Disable auto-scroll on user interaction
function handleUserInteraction() {
  if (autoScrollEnabled.value) {
    autoScrollEnabled.value = false
    stopAutoScroll()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  
  // Detect mobile - prioritize screen width for better tablet handling
  isMobile.value = window.innerWidth < 768
  
  // Get reference to the table scroll container
  tableScrollRef.value = document.querySelector('.table-scroll')
  
  // Add scroll and interaction event listeners to disable auto-scroll
  if (tableScrollRef.value) {
    tableScrollRef.value.addEventListener('wheel', handleUserInteraction)
    tableScrollRef.value.addEventListener('touchstart', handleUserInteraction)
  }
  
  timer = setInterval(() => {
    now.value = Date.now()
    const cutoff = Date.now() - 30 * 1000
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
  }, 1000)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  
  if (tableScrollRef.value) {
    tableScrollRef.value.removeEventListener('wheel', handleUserInteraction)
    tableScrollRef.value.removeEventListener('touchstart', handleUserInteraction)
  }
  
  stopAutoScroll()
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}

// Add this function to handle checkpoint text display
function getCheckpointDisplayText(marker) {
  if (!marker.text) return ''
  
  // Check for patterns like CP72, PK32, PK 27 and extract the number
  const pkSpaceMatch = marker.text.match(/^PK (\d+)$/)
  if (pkSpaceMatch) return pkSpaceMatch[1]

  const cpSpaceMatch = marker.text.match(/^CP (\d+)$/)
  if (cpSpaceMatch) return cpSpaceMatch[1]
  
  const cpMatch = marker.text.match(/^CP(\d+)$/)
  if (cpMatch) return cpMatch[1]
  
  const pkMatch = marker.text.match(/^PK(\d+)$/)
  if (pkMatch) return pkMatch[1]
  
  // If no match, return original text if length < 4
  if (marker.text.length < 4) return marker.text
  return ''
}
</script>

<template>
  <div>
    <div class="header-container">
      
      <!-- Legend button -->
      <button class="legend-button" @click="toggleLegendPopup" title="Pokaż legendę">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 16v-4m0-4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Legenda
      </button>
<!-- Auto-scroll toggle button -->
      <button 
        class="auto-scroll-button" 
        :class="{ active: autoScrollEnabled }"
        @click="toggleAutoScroll" 
        title="Włącz/wyłącz automatyczne przewijanie"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 10l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M7 6l5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Auto-scroll
      </button>

      <!-- Search functionality -->
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="form-control search-input" 
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>
        
        <!-- Search dropdown -->
        <div v-if="showDropdown" class="search-dropdown">
          <div 
            v-for="result in filteredResults" 
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>

      <img 
        v-if="resultsStore.event?.event?.logo_file" 
        :src="`https://sledzgps.pl/files/logo/${resultsStore.event.event.logo_file}`" 
        alt="Event Logo" 
        class="event-logo"
      />
      
      
    </div>
    
    <!-- Legend backdrop and popup -->
    <div v-if="showLegendPopup" class="legend-backdrop" @click="closeLegendPopup"></div>
    <div v-if="showLegendPopup" class="legend-popup">
      <div class="legend-popup-header">
        <h5>Legenda</h5>
        <button class="close-btn" @click="closeLegendPopup">×</button>
      </div>
      <div class="legend-popup-content">
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #dc3545;">■</span>
          <span>Punkt zaliczony</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint empty">□</span>
          <span>Punkt niezaliczony</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #dc3545;">■</span>
          <span>Rower</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #28a745;">■</span>
          <span>Bieg/Trekking</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #007bff;">■</span>
          <span>Kajak</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled" style="background-color: #6f42c1;">■</span>
          <span>Inna dyscyplina</span>
        </div>
        <div class="legend-item">
          <span class="checkpoint filled star" style="background-color: #fd7e14;">★</span>
          <span>Zadanie specjalne</span>
        </div>
      </div>
    </div>
    
    <div class="table-scroll">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th>Start</th>
            <th v-if="isSingleStage" class="sortable-header" @click="toggleSort('checkpoints')" title="Kliknij aby sortować">
              Zaliczone
              <span class="sort-indicator" v-if="showCheckpointsSortIndicator">
                {{ checkpointsSortDirection }}
              </span>
            </th>
            <th v-for="(stage, index) in stages" :key="index" class="stage-column">
              {{ isSingleStage ? 'Punkty kontrolne' : `Etap ${index + 1}` }}
            </th>
            <th class="sortable-header" @click="toggleSort('time')" title="Kliknij aby sortować">
              Czas całk.
              <span class="sort-indicator" v-if="showTimeSortIndicator">
                {{ timeSortDirection }}
              </span>
            </th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <!-- Locked top 3 finishers (when autoscroll is enabled) -->
          <tr
            v-for="result in top3Finishers"
            :key="`locked-${result.id}`"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id], 'locked-row': true }"
          >
            <td>{{ resultPositions.get(result.id) }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            
            <!-- Completed column for single-stage competitions -->
            <td v-if="isSingleStage" class="completed-cell">
              {{ countCompletedCheckpoints(result) }}/{{ totalCheckpoints }}
            </td>
            
            <!-- Stage columns -->
            <td v-for="(stage, stageIndex) in stages" :key="stageIndex" class="stage-cell" :class="{ 'single-stage': isSingleStage }">
              <div class="stage-content">
                <!-- Checkpoints as squares -->
                <div class="checkpoints" :class="{ 'single-stage': isSingleStage }">
                  <span
                    v-for="marker in stage.markers"
                    :key="marker.id"
                    class="checkpoint"
                    :class="{ 
                      'filled': isCheckpointCompleted(result, marker.id),
                      'empty': !isCheckpointCompleted(result, marker.id),
                      'star': marker.type === 94,
                      'show-text': getCheckpointDisplayText(marker) && marker.type !== 94
                    }"
                    :style="{ 
                      backgroundColor: isCheckpointCompleted(result, marker.id) ? getCheckpointColor(marker.type) : 'transparent',
                      borderColor: getCheckpointColor(marker.type)
                    }"
                    :title="marker.text"
                    @mouseenter="handleCheckpointHover($event, result, marker)"
                    @mouseleave="handleCheckpointLeave"
                    @click="handleCheckpointClick(result, marker)"
                  >
                    <span v-if="marker.type === 94 && isCheckpointCompleted(result, marker.id)" class="star-icon">★</span>
                    <span v-else-if="marker.type === 94 && !isCheckpointCompleted(result, marker.id)" class="star-icon-empty">☆</span>
                    <span v-else-if="getCheckpointDisplayText(marker)" class="checkpoint-text">{{ getCheckpointDisplayText(marker) }}</span>
                    <span v-else-if="isCheckpointCompleted(result, marker.id)">■</span>
                    <span v-else>□</span>
                  </span>
                </div>
                
                <!-- Stage time -->
                <div v-if="!isSingleStage" class="stage-time">
                  {{ getStageTime(result, stage, stageIndex) ? getHHMMSSString(getStageTime(result, stage, stageIndex)) : '--:--:--' }}
                </div>
              </div>
            </td>
            
            <!-- Total time -->
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">
                {{ result.abs ? "DNS" : "DNF" }}
              </span>
              <span v-else :class="getResultClass(result)">
                {{ getTotalTime(result) ? getHHMMSSString(getTotalTime(result)) : '--:--:--' }}
              </span>
            </td>
            
            <!-- Gap to leader -->
            <td class="gap-cell">
              <div v-html="formatGapTime(resultsStore.getGapToLeader(result)).replace('\n', '<br/>')"></div>
            </td>
          </tr>
          
          <!-- Scrollable results (all results when autoscroll is off, or remaining results when on) -->
          <tr
            v-for="result in scrollableResults"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>{{ resultPositions.get(result.id) }}</td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            
            <!-- Completed column for single-stage competitions -->
            <td v-if="isSingleStage" class="completed-cell">
              {{ countCompletedCheckpoints(result) }}/{{ totalCheckpoints }}
            </td>
            
            <!-- Stage columns -->
            <td v-for="(stage, stageIndex) in stages" :key="stageIndex" class="stage-cell" :class="{ 'single-stage': isSingleStage }">
              <div class="stage-content">
                <!-- Checkpoints as squares -->
                <div class="checkpoints" :class="{ 'single-stage': isSingleStage }">
                  <span
                    v-for="marker in stage.markers"
                    :key="marker.id"
                    class="checkpoint"
                    :class="{ 
                      'filled': isCheckpointCompleted(result, marker.id),
                      'empty': !isCheckpointCompleted(result, marker.id),
                      'star': marker.type === 94,
                      'show-text': getCheckpointDisplayText(marker) && marker.type !== 94
                    }"
                    :style="{ 
                      backgroundColor: isCheckpointCompleted(result, marker.id) ? getCheckpointColor(marker.type) : 'transparent',
                      borderColor: getCheckpointColor(marker.type)
                    }"
                    :title="marker.text"
                    @mouseenter="handleCheckpointHover($event, result, marker)"
                    @mouseleave="handleCheckpointLeave"
                    @click="handleCheckpointClick(result, marker)"
                  >
                    <span v-if="marker.type === 94 && isCheckpointCompleted(result, marker.id)" class="star-icon">★</span>
                    <span v-else-if="marker.type === 94 && !isCheckpointCompleted(result, marker.id)" class="star-icon-empty">☆</span>
                    <span v-else-if="getCheckpointDisplayText(marker)" class="checkpoint-text">{{ getCheckpointDisplayText(marker) }}</span>
                    <span v-else-if="isCheckpointCompleted(result, marker.id)">■</span>
                    <span v-else>□</span>
                  </span>
                </div>
                
                <!-- Stage time -->
                <div v-if="!isSingleStage" class="stage-time">
                  {{ getStageTime(result, stage, stageIndex) ? getHHMMSSString(getStageTime(result, stage, stageIndex)) : '--:--:--' }}
                </div>
              </div>
            </td>
            
            <!-- Total time -->
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">
                {{ result.abs ? "DNS" : "DNF" }}
              </span>
              <span v-else :class="getResultClass(result)">
                {{ getTotalTime(result) ? getHHMMSSString(getTotalTime(result)) : '--:--:--' }}
              </span>
            </td>
            
            <!-- Gap to leader -->
            <td class="gap-cell">
              <div v-html="formatGapTime(resultsStore.getGapToLeader(result)).replace('\n', '<br/>')"></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Tooltip for desktop -->
    <div 
      v-if="hoveredCheckpoint && !isMobile" 
      class="checkpoint-tooltip"
      :style="{ left: tooltipPosition.x + 10 + 'px', top: tooltipPosition.y + 10 + 'px' }"
    >
      <div class="tooltip-content">
        <strong>{{ hoveredCheckpoint.markerText }}</strong><br>
        Czas: {{ hoveredCheckpoint.time }}
      </div>
    </div>
    
    <!-- Modal for mobile -->
    <div v-if="hoveredCheckpoint && isMobile" class="tooltip-modal" @click="closeTooltip">
      <div class="modal-content" @click.stop>
        <button class="close-btn" @click="closeTooltip">×</button>
        <div class="tooltip-content">
          <strong>{{ hoveredCheckpoint.markerText }}</strong><br>
          Czas: {{ hoveredCheckpoint.time }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.result-upcoming {
  font-style: italic;
}

.table {
  text-align: left;
}

.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}

@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

/* Sortable header styles */
.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #e9ecef !important;
}

.sort-indicator {
  font-size: 0.8em;
  margin-left: 5px;
  display: inline-block;
}

/* Completed column */
.completed-cell {
  text-align: center;
  font-weight: 600;
  color: #007bff;
}

/* Header container */
.header-container {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: space-between;
}

/* Event logo */
.event-logo {
  max-height: 150px;
  max-width: auto;
  object-fit: contain;
}

/* Legend button */
.legend-button {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
  display: none;
}

.legend-button:hover {
  background-color: #0056b3;
}

.legend-button svg {
  width: 20px;
  height: 20px;
}

/* roll button */
.auto-scroll-button {
  display: none;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.auto-scroll-button:hover {
  background-color: #5a6268;
}

.auto-scroll-button.active {
  background-color: #28a745;
}

.auto-scroll-button.active:hover {
  background-color: #218838;
}

.auto-scroll-button svg {
  width: 20px;
  height: 20px;
}

/* Legend popup */
.legend-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 2001;
  min-width: 300px;
  max-width: 400px;
}

/* Legend backdrop */
.legend-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
}

.legend-popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
}

.legend-popup-header h5 {
  margin: 0;
  font-size: 1.1em;
}

.legend-popup-header .close-btn {
  position: static;
  padding: 0;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  line-height: 1;
}

.legend-popup-header .close-btn:hover {
  color: #000;
}

.legend-popup-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95em;
}

/* Search styles */
.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  display: flex;
  align-items: center;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

/* Stage column */
.stage-column {
  text-align: center;
  min-width: 150px;
}

/* Single stage gets wider column */
.stage-cell.single-stage {
  min-width: 300px;
}

.stage-cell {
  text-align: center;
  padding: 8px;
}

.stage-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.checkpoints {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  justify-content: center;
  max-width: 200px;
}

/* Wider checkpoint container for single stage */
.checkpoints.single-stage {
  max-width: 500px;
}

.checkpoint {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid;
  border-radius: 3px;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
}

.checkpoint:hover {
  transform: scale(1.2);
  z-index: 5;
}

.checkpoint.filled {
  color: white;
  font-weight: bold;
}

.checkpoint.empty {
  background-color: white;
  color: #666;
}

.checkpoint.star .star-icon {
  font-size: 14px;
  color: white;
}

.checkpoint.empty .star-icon-empty {
  font-size: 14px;
  color: #666;
}

.checkpoint.show-text .checkpoint-text {
  font-size: 9px;
  font-weight: bold;
  color: white;
}

.checkpoint.empty .checkpoint-text {
  font-size: 9px;
  font-weight: bold;
  color: #666;
}

.stage-time {
  font-size: 0.95em;
  font-weight: 500;
  margin-top: 3px;
}

.gap-cell {
  white-space: pre-line;
  font-size: 0.9em;
}

/* Tooltip */
.checkpoint-tooltip {
  position: fixed;
  background-color: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9em;
  z-index: 1000;
  pointer-events: none;
  max-width: 250px;
}

.tooltip-content {
  line-height: 1.4;
}

/* Mobile modal */
.tooltip-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 300px;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.close-btn:hover {
  color: #000;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .checkpoint {
    width: 18px;
    height: 18px;
    font-size: 11px;
  }
  
  .stage-column {
    min-width: 120px;
  }
  
  .stage-cell.single-stage {
    min-width: 250px;
  }
  
  .checkpoints.single-stage {
    max-width: 350px;
  }
  
  .header-container {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-container h1 {
    font-size: 1.2rem;
  }
  
  .event-logo {
    max-height: 80px;
    max-width: 120px;
  }
  
  .legend-button,
  .auto-scroll-button {

    font-size: 0.85em;
    padding: 6px 12px;
  }
  
  .legend-popup {
    max-width: 90%;
    min-width: 280px;
  }
}

/* Desktop - even wider for single stage */
@media (min-width: 1200px) {
  .stage-cell.single-stage {
    min-width: 400px;
  }
  
  .checkpoints.single-stage {
    max-width: 600px;
  }
}

/* Locked row styles - sticky positioning when autoscroll is enabled */
.locked-row {
  position: sticky;
  z-index: 5;
  background-color: #fff9e6 !important;
  border-left: 4px solid #ffc107;
}

.locked-row td {
  background-color: #fff9e6 !important;
  font-weight: 600;
}

/* Position locked rows below the header using fixed calculations */
/* First locked row positioned right below header */
.locked-row:nth-of-type(1) {
  top: 45px;
}

/* Second locked row stacked below first */
.locked-row:nth-of-type(2) {
  top: 90px;
}

/* Third locked row stacked below second */
.locked-row:nth-of-type(3) {
  top: 135px;
}
</style>
